import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    Output,
    ViewChild
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";
import {DriversService} from "@atl/admin/drivers/services";
import {catchError, first, map, skipWhile, switchMap, withLatestFrom} from "rxjs/operators";
import {BehaviorSubject, forkJoin, fromEvent, of} from "rxjs";
import {IDriverTreeItem} from "@atl/admin/drivers/interfaces";
import {TypesUtils} from "@atl/shared/utils";
import {ModalService} from "@atl/shared/services/modal.service";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {DriverElementsDialogComponent} from "@atl/modules/modals/driver-elements-dialog/driver-elements-dialog.component";
import {GetChildrenFn} from "@atl/modules/tree/interfaces/tree.interface";

@UntilDestroy()
@Component({
    selector: 'lta-driver-element-select',
    templateUrl: 'driver-element-select.component.html',
    styleUrls: ['driver-element-select.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: DriverElementSelectComponent,
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DriverElementSelectComponent implements ControlValueAccessor, AfterViewInit {
    @ViewChild('inputTarget', {static: true}) inputTarget: ElementRef;
    @ViewChild('wrapper') wrapper: ElementRef;

    @Input() disabled: boolean = false;
    @Output() onUnselectDriverElement = new EventEmitter();
    @Input() disabledBtns: boolean = false

    public menuItems: string[] = ['LACERTA_DRIVER_PROTOCOL_CONN_STATUS', 'LACERTA_DRIVER_TIMEOUT']
    public isSelectOpen: boolean = false
    public driverElementId: string
    public initialDriverElementId: string
    public showModal: boolean
    public driverId$ = new BehaviorSubject<number>(null)
    public hasTree$ = this.driverId$
        .pipe(
            switchMap(id => {
                return forkJoin([of(id), this.driversService.drivers$.pipe(skipWhile(d => !d.length), first())])
            }),
            map(([id, drivers]) => {
                return drivers.find(d => d.id === id)?.driver_info.has_tree
            })
        )
    public isOverflowed = false;
    private onChange: (driverElementId) => {};
    private onTouched: () => {};
    private touched: Boolean;

    constructor(
        private driversService: DriversService,
        private modalService: ModalService,
        private cd: ChangeDetectorRef
    ) {
    }

    @Input() set driverId(id: number) {
        this.driverId$.next(id)
    };

    @HostListener('document:click', ['$event.target'])
    onDocumentClick(target: HTMLElement) {
        if (!target.closest('#driver-element-select')) {
            this.isSelectOpen = false
        }
    }

    writeValue(driverElementId: string) {
        this.driverElementId = driverElementId;
        this.initialDriverElementId = driverElementId;
        this.cd.markForCheck()
        this.checkTextOverflow();
    }

    registerOnChange(onChange: any) {
        this.onChange = onChange;
    }

    registerOnTouched(onTouched: any) {
        this.onTouched = onTouched;
    }

    public onSelect($event: IDriverTreeItem) {
        this.markAsTouched();
        this.driverElementId = $event.object_id;
        this.onChange($event.object_id);
        this.showModal = false;
        this.cd.markForCheck()
    }

    public onInputValueChange(driverId: string) {
        this.markAsTouched();
        this.driverElementId = driverId;
        this.onChange(this.driverElementId);
        this.checkTextOverflow();
    }

    isFocused(el: Element) {
        return document.activeElement === el;
    }

    public loadTreeChildrenFn: GetChildrenFn = (treeItem) => {
        return this.driverId$
            .pipe(first(),
                switchMap(value => this.driversService.getDriverTreeChildren(value, treeItem.item.id)
                    .pipe(
                        map(res => {
                            return res.tree.map(d => TypesUtils.driverTreeItemToNodeData(d))
                        })
                    )
                ))
    }

    public createSelectDialog(e: Event) {
        e.stopPropagation()
        const id = 'select'
        this.driversService.getDriverTree(this.driverId$.value)
            .pipe(
                map(value => value?.tree),
                catchError((err, caught) => {
                    this.showModal = false
                    return of(null)
                })
            ).pipe(first())
            .subscribe(v => {
                this.markAsTouched()
                const dialogRef = this.modalService.create<DriverElementsDialogComponent>(id, DriverElementsDialogComponent, {
                    inputs: {
                        withSelect: true,
                        loadChildrenFn: this.loadTreeChildrenFn,
                        driverTreeItems: v
                    }
                })
                if (!dialogRef) return
                dialogRef.instance.onCancel.pipe(untilDestroyed(dialogRef.instance)).subscribe(() => {
                    this.modalService.remove(id)
                })
                dialogRef.instance.onSave.pipe(untilDestroyed(dialogRef.instance)).subscribe((v) => {
                    this.onSelect(v)
                    this.modalService.remove(id)
                })
            })

    }

    public toggleMenu(e: Event) {
        e.stopPropagation()
        this.isSelectOpen = !this.isSelectOpen
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.checkTextOverflow()
        }, 200);
        fromEvent(this.wrapper.nativeElement, 'click')
            .pipe(withLatestFrom(this.hasTree$))
            .subscribe(([isClick, hasTree]) => {
                if (isClick && hasTree) {
                    this.isSelectOpen = !this.isSelectOpen
                }
            })
    }

    ngOnDestroy() {
        this.modalService.remove('select');
    }

    public selectItem(e: Event, value: string) {
        e.stopPropagation();
        this.markAsTouched();
        this.driverElementId = value;
        this.isSelectOpen = false;
        this.onChange(value);
        this.cd.markForCheck();
        this.checkTextOverflow();
    }

    public removeSelection() {
        if (this.disabledBtns) return
        this.onUnselectDriverElement.emit()
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    private markAsTouched() {
        if (!this.touched) {
            this.onTouched();
            this.touched = true;
        }
    }

    private checkTextOverflow() {
        setTimeout(() => {
            this.isOverflowed = this.inputTarget.nativeElement.scrollWidth > this.inputTarget.nativeElement.clientWidth;
            this.cd.markForCheck();
        }, 100)
    }
}
